import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { withStyles } from 'theme/withStyles';
import uiActions from 'core/ui/actions';
import groupActions from 'core/groups/actions';
import Button from 'common/Button';
import backButtonStyles from 'groups/styles/backButton';
import isIE11 from 'utils/isIE11';
import Spinner from 'common/Spinner';
import BottomContent from 'common/BottomContent';
import ErrorMsg from 'common/ErrorMsg';
import hasError from 'utils/hasError';
import pick from 'lodash/pick';
import RawHtml from '../common/RawHtml';
import iconKeyboard from './icons/keyboard.svg';
import iconLicenseBack from './icons/licenseBack.svg';
import iconLicenseFront from './icons/licenseFront.svg';

import commonStyles from './styles';
import buttonRowStyles from './styles/buttonRow';

const styles = (theme) => {
  const common = commonStyles(theme);

  return {
    ...common,
    back: backButtonStyles(theme),
    button: {
      margin: [0, 0, theme.spacing.xs, 0],
    },
    buttonRow: {
      ...buttonRowStyles(theme),
    },
    buttons: {
      display: 'flex',
      flexDirection: 'column',
      margin: [0, 0, theme.spacing.xl, 0],
    },
    inputBack: {
      left: '-200%',
      position: 'absolute',
      top: '-200%',
      zIndex: -1,
    },
    keyboard: {
      '&:before': {
        background: isIE11
          ? `transparent url(${iconKeyboard}) 0 50% no-repeat`
          : theme.palette.icon,
        maskImage: isIE11 ? 'none' : `url(${iconKeyboard})`,
      },
    },
    licenseBack: {
      '&:before': {
        background: isIE11
          ? `transparent url(${iconLicenseBack}) 0 50% no-repeat`
          : theme.palette.icon,
        maskImage: isIE11 ? 'none' : `url(${iconLicenseBack})`,
      },
    },
    licenseFront: {
      '&:before': {
        background: isIE11
          ? `transparent url(${iconLicenseFront}) 0 50% no-repeat`
          : theme.palette.icon,
        maskImage: isIE11 ? 'none' : `url(${iconLicenseFront})`,
      },
      display: 'block',
    },
    mdpcOption: {
      '&:active, &:focus, &:hover': {
        background: 'inherit',
        color: theme.links.link.color,
      },
      '&:before': {
        content: '" "',
        height: 32,
        left: 0,
        marginTop: -16,
        position: 'absolute',
        top: '50%',
        width: 32,
      },
      background: 'transparent none',
      border: 'none',
      borderRadius: 0,
      color: theme.links.link.color,
      fontWeight: theme.typography.fontWeights.regular,
      margin: [0, 25],
      padding: [25, 0, 25, 50],
      position: 'relative',
      textAlign: 'left',
    },
    panel: {
      margin: [],
    },
  };
};

const DlBare = ({
  activePanel,
  backAllowed,
  backButtonLabel,
  bottomContent,
  children,
  classes,
  continueAllowed,
  disableButton,
  hasFields,
  keyboardGroup,
  labelMobile,
  loading,
  metaResources,
  name: groupName,
  onBack,
  onContinueClick,
  onSave,
  resources,
  saveAndContinueAllowed,
  saveButtonLabel,
  setActiveGroup,
  setActivePanel,
  subText,
  upload,
  wrapClass,
}) => {
  const name = groupName.toLowerCase();
  const fileBackRef = useRef(null);
  const fileFrontRef = useRef(null);
  const [edit, setEdit] = useState(false);
  return (
    <>
      <legend
        className={classnames('grp-label', classes.label)}
        role={!hasFields ? 'presentation' : undefined}
      >
        <RawHtml type="span" className={classnames('mobile', classes.mobileLabel)}>
          {labelMobile}
        </RawHtml>
      </legend>
      <div className={wrapClass}>
        <div
          className={classnames('mpdc-panel panel-photo-back', classes.panel, {
            hidden: activePanel !== 'back',
          })}
        >
          <ErrorMsg name={groupName} type="group" />

          <RawHtml type="div" className={classnames('content', classes.content)}>
            {subText}
          </RawHtml>

          <input
            name="mpdc-back"
            className={classnames('photo-capture-back', classes.inputBack)}
            type="file"
            ref={fileBackRef}
            onChange={(e) => {
              upload(e.target.files);
              fileBackRef.current.value = '';
            }}
          />

          <div className={classnames('btn-group', classes.buttons)}>
            <Button
              className={classnames(
                'btn-photocapture-photo-back',
                classes.mdpcOption,
                classes.licenseBack,
              )}
              aria-label="Capture"
              onClick={() => fileBackRef.current.click()}
            >
              Scan the barcode
            </Button>
            <Button
              className={classnames(
                'btn-photocapture-keyboard',
                classes.mdpcOption,
                classes.keyboard,
              )}
              aria-label="Keyboard"
              onClick={() => setActiveGroup(keyboardGroup)}
            >
              Use the keyboard
            </Button>
          </div>

          <RawHtml
            className={classnames('btn btn-mobile btn-tertiary btn-grp-back', classes.back)}
            data-name="btn-group-back"
            type="button"
            onClick={onBack}
            variant="secondary"
          >
            {backButtonLabel}
          </RawHtml>
        </div>

        <div
          className={classnames('mpdc-panel panel-info summary', classes.panel, {
            edit,
            hidden: activePanel !== 'summary',
          })}
        >
          <legend className={classnames('grp-label', classes.label)}>
            <RawHtml type="span" className={classnames('mobile', classes.mobileLabel)}>
              {resources.confirmationTitle}
            </RawHtml>
          </legend>
          <RawHtml type="div" className={classnames('content', classes.content)}>
            {resources.confirmationSubtitle}
          </RawHtml>

          <div
            className={classnames('region-fields', classes.fields)}
            onClick={() => setEdit(true)}
          >
            {children}
          </div>

          <div className={classnames('row btn-row-group', `btn-group-${name}`, classes.buttonRow)}>
            {continueAllowed && (
              <Button
                className={classnames(
                  'btn btn-mobile mt-5 btn-looks-good',
                  `btn-next-${name}`,
                  'mt-5',
                  classes.button,
                  {
                    loading,
                  },
                )}
                data-name="btn-grp-next"
                onClick={onContinueClick}
                disabled={disableButton}
              >
                <RawHtml type="span">{resources.confirmationButtonLabel}</RawHtml>{' '}
                <i className="icon-arrow-thin-right" aria-hidden="true" />
                <Spinner loading={loading} />
              </Button>
            )}
            {saveAndContinueAllowed && (
              <RawHtml
                className={classnames(
                  'btn btn-mobile btn-tertiary btn-grp-save-and-continue',
                  classes.button,
                )}
                data-name="btn-grp-save-and-continue"
                type="button"
                disabled={disableButton}
                onClick={onSave}
                variant="secondary"
              >
                {saveButtonLabel}
              </RawHtml>
            )}
          </div>
        </div>

        <div
          className={classnames('mpdc-panel panel-photo-front', classes.panel, {
            hidden: activePanel !== 'front',
          })}
        >
          <legend className={classnames('grp-label', classes.label)}>
            <RawHtml type="span" className={classnames('mobile', classes.mobileLabel)}>
              {metaResources.iduploadWeGotYourInfo}
            </RawHtml>
          </legend>

          <input
            name="mpdc-front"
            className={classnames('photo-capture-front', classes.inputBack)}
            type="file"
            ref={fileFrontRef}
            onChange={(e) => {
              upload(e.target.files, false);
              fileFrontRef.current.value = '';
            }}
          />

          <RawHtml type="div" className={classnames('content', classes.content)}>
            {metaResources.iduploadScanFrontMessage}
          </RawHtml>

          <div className={classnames('btn-group', classes.buttons)}>
            <Button
              className={classnames(
                'btn-photocapture-photo-front btn-capture-front',
                classes.mdpcOption,
                classes.licenseFront,
              )}
              data-name="btn-capture-front"
              onClick={() => fileFrontRef.current.click()}
            >
              Capture Front Image
            </Button>
          </div>

          <BottomContent className="bottomContent">{bottomContent}</BottomContent>

          <div className={classnames('row btn-row-group', `btn-group-${name}`, classes.buttonRow)}>
            {saveAndContinueAllowed && (
              <RawHtml
                className="btn btn-mobile btn-tertiary btn-grp-save-and-continue"
                data-name="btn-grp-save-and-continue"
                type="button"
                onClick={onSave}
                variant="secondary"
              >
                {saveButtonLabel}
              </RawHtml>
            )}

            {backAllowed && (
              <RawHtml
                className={classnames(
                  'btn btn-mobile btn-tertiary btn-grp-back mt-5',
                  classes.back,
                )}
                data-name="btn-grp-back"
                type="button"
                onClick={() => setActivePanel('back')}
                variant="secondary"
              >
                {backButtonLabel}
              </RawHtml>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

DlBare.propTypes = {
  activePanel: PropTypes.string,
  backAllowed: PropTypes.bool.isRequired,
  backButtonLabel: PropTypes.string,
  bottomContent: PropTypes.string,
  children: PropTypes.node.isRequired,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  continueAllowed: PropTypes.bool,
  disableButton: PropTypes.bool.isRequired,
  hasFields: PropTypes.bool,
  keyboardGroup: PropTypes.string.isRequired,
  labelMobile: PropTypes.string,
  loading: PropTypes.bool.isRequired,
  metaResources: PropTypes.objectOf(PropTypes.string).isRequired,
  name: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  onContinueClick: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  resources: PropTypes.objectOf(PropTypes.string),
  saveAndContinueAllowed: PropTypes.bool,
  saveButtonLabel: PropTypes.string,
  setActiveGroup: PropTypes.func.isRequired,
  setActivePanel: PropTypes.func.isRequired,
  subText: PropTypes.string,
  upload: PropTypes.func.isRequired,
  wrapClass: PropTypes.string.isRequired,
};

DlBare.defaultProps = {
  activePanel: 'back',
  backButtonLabel: '',
  bottomContent: '',
  continueAllowed: false,
  hasFields: false,
  labelMobile: '',
  resources: {},
  saveAndContinueAllowed: false,
  saveButtonLabel: '',
  subText: '',
};

const mapStateToProps = ({ fields, groups, page, resources, ui }, { name }) => {
  const group = groups[name];
  const loading = !!(ui.groupLoading || ui.fieldLoading || ui.appLoading || ui.pageLoading);
  return {
    activePanel: ui.activePanel || 'back',
    backAllowed: page.backAllowed || Object.keys(groups)[0] !== name,
    backButtonLabel: group.backButtonLabel,
    bottomContent: group.bottomContent,
    continueAllowed: page.continueAllowed,
    disableButton: loading || hasError(pick(fields, group.fields)) || ui.menuOpen || false,
    hasFields: group?.fields?.length > 0 ?? false,
    labelMobile: group.labelMobile,
    loading,
    metaResources: resources,
    resources: group.resources,
    saveAndContinueAllowed: page.saveAndContinueAllowed,
    saveButtonLabel: page.saveButtonLabel,
    subText: group.subText,
  };
};

const mapDispatchToProps = (dispatch, { name }) => ({
  onBack: () => {
    dispatch(groupActions.back());
  },
  onContinueClick: () => {
    dispatch(groupActions.next(name));
  },
  onSave: () => {
    dispatch(groupActions.save(name));
  },
  setActiveGroup: (group) => {
    dispatch(uiActions.setActiveGroup(group));
  },
  setActivePanel: (panel) => {
    dispatch(uiActions.setActivePanel(panel));
  },
  upload: (files, back) => {
    dispatch(groupActions.upload(files, back));
  },
});

const DlStyled = withStyles(styles)(DlBare);

export default connect(mapStateToProps, mapDispatchToProps)(DlStyled);
